class ProductStickyBarNew extends HTMLElement {
  connectedCallback() {
    this.requiredBodyClass = this.dataset.requireBodyClass || null;
    this.initialized = false;

    if (this.requiredBodyClass) {
      this.syncAbTestVisibility = this.syncAbTestVisibility.bind(this);
      this.syncAbTestVisibility();

      if (!this.initialized) {
        this.abTestObserver = new MutationObserver(this.syncAbTestVisibility);
        this.abTestObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return;
      }
    }

    this.setup();
  }

  disconnectedCallback() {
    this.abTestObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.abortController?.abort();
  }

  syncAbTestVisibility() {
    const shouldShow = document.body.classList.contains(this.requiredBodyClass);

    if (shouldShow) {
      this.removeAttribute('hidden');

      if (!this.initialized) {
        this.setup();
      }
    } else {
      this.setAttribute('hidden', '');
      this.classList.remove('is-visible');
    }
  }

  setup() {
    if (this.initialized) return;
    this.initialized = true;

    this.formId = this.getAttribute('form');
    this.observeSelector = this.getAttribute('observe') || '.buy-box-wrapper';
    this.qtyKey = `psbNewQty:${this.formId || this.id || 'default'}`;
    this.abortController = new AbortController();
    this.formPastViewport = false;
    this.footerNotVisible = true;

    const { signal } = this.abortController;

    this.onClick = this.onClick.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onProductRerender = this.onProductRerender.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('focusin', this.onFocusIn, { signal });
    this.addEventListener('focusout', this.onFocusOut, { signal });
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('mouseover', this.onMouseOver, { signal });
    document.addEventListener('variant:change', this.onVariantChange, { signal });

    const form = this.formId ? document.forms[this.formId] : null;
    if (form) {
      form.addEventListener('product:rerender', this.onProductRerender, { signal });
    }

    this.setupVisibilityObserver();
    // Sync before first paint so product:rerender does not flash opacity 0→1
    this.syncVisibilityNow();
    this.restoreQty();

    // After <product-rerender> replaceWith, the previous instance is gone —
    // re-init Shop Pay on the newly mounted markup.
    if (window.Shopify?.PaymentButton?.init) {
      requestAnimationFrame(() => Shopify.PaymentButton.init());
    }
  }

  setupVisibilityObserver() {
    this.observeTarget =
      document.querySelector(this.observeSelector) ||
      document.forms[this.getAttribute('observe-form')] ||
      document.forms[this.formId];

    this.footerElement = document.querySelector('.shopify-section--footer');

    if (!this.observeTarget) return;

    this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange);
    this.visibilityObserver.observe(this.observeTarget);
    if (this.footerElement) {
      this.visibilityObserver.observe(this.footerElement);
    }
  }

  /**
   * Immediate visibility from layout (IO callbacks are async and cause flicker
   * when <product-rerender> replaces this element on variant change).
   */
  syncVisibilityNow() {
    if (!this.observeTarget) return;

    const targetRect = this.observeTarget.getBoundingClientRect();
    this.formPastViewport = targetRect.bottom < 0;

    if (this.footerElement) {
      const footerRect = this.footerElement.getBoundingClientRect();
      this.footerNotVisible = !(footerRect.top < window.innerHeight && footerRect.bottom > 0);
    }

    this.applyVisibility({ suppressTransition: true });
  }

  applyVisibility({ suppressTransition = false } = {}) {
    const shouldShow = this.formPastViewport && this.footerNotVisible;

    if (suppressTransition) {
      this.style.transition = 'none';
    }

    this.classList.toggle('is-visible', shouldShow);

    if (suppressTransition) {
      // Force reflow so the next style change can animate again
      void this.offsetWidth;
      this.style.transition = '';
    }
  }

  onVisibilityChange(entries) {
    const targetEntry = entries.find((entry) => entry.target === this.observeTarget);
    const footerEntry = entries.find((entry) => entry.target === this.footerElement);

    if (targetEntry) {
      // Show after scrolling past the buy box / observe target
      this.formPastViewport = !targetEntry.isIntersecting && targetEntry.boundingClientRect.bottom < 0;
    }

    if (footerEntry) {
      this.footerNotVisible = !footerEntry.isIntersecting;
    }

    this.applyVisibility();
  }

  get qtyInput() {
    return this.querySelector('[data-psb-qty-input]');
  }

  get hiddenQty() {
    return this.querySelector('[data-psb-hidden-qty]');
  }

  get shopQty() {
    return this.querySelector('[data-psb-shop-qty]');
  }

  get atcId() {
    return this.querySelector('[data-psb-atc-id]');
  }

  get shopId() {
    return this.querySelector('[data-psb-shop-id]');
  }

  get shopVisual() {
    return this.querySelector('[data-psb-shop-visual]');
  }

  formatQty(val) {
    let n = parseInt(val, 10);
    if (isNaN(n) || n < 1) n = 1;
    return n;
  }

  padQty(n) {
    return n < 10 ? `0${n}` : String(n);
  }

  formatMoney(cents) {
    const amount = (Math.round(cents) / 100).toFixed(2);
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;

    if (!format) {
      try {
        return new Intl.NumberFormat(Shopify.locale, {
          style: 'currency',
          currency: Shopify.currency.active
        }).format(cents / 100);
      } catch (e) {
        return `$${amount}`;
      }
    }

    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
  }

  updatePriceDisplay(qty) {
    this.querySelectorAll('[data-psb-price]').forEach((priceEl) => {
      const unitPrice = parseInt(priceEl.dataset.unitPrice, 10);
      if (isNaN(unitPrice)) return;

      const comparePrice = parseInt(priceEl.dataset.comparePrice, 10);
      const salePrice = priceEl.querySelector('sale-price');
      if (salePrice) {
        const srOnly = salePrice.querySelector('.sr-only');
        salePrice.innerHTML = '';
        if (srOnly) salePrice.appendChild(srOnly);
        salePrice.appendChild(document.createTextNode(this.formatMoney(unitPrice * qty)));
      }

      const compareValue = priceEl.querySelector('compare-at-price .c-value');
      if (compareValue && !isNaN(comparePrice) && comparePrice > 0) {
        compareValue.textContent = this.formatMoney(comparePrice * qty);
      }
    });
  }

  applyQty(val) {
    const n = this.formatQty(val);

    try {
      sessionStorage.setItem(this.qtyKey, String(n));
    } catch (e) {}

    if (this.qtyInput) {
      this.qtyInput.dataset.value = String(n);
      this.qtyInput.value = this.padQty(n);
    }
    if (this.hiddenQty) this.hiddenQty.value = String(n);
    if (this.shopQty) this.shopQty.value = String(n);

    this.updatePriceDisplay(n);
  }

  restoreQty() {
    let saved = 1;
    try {
      saved = this.formatQty(sessionStorage.getItem(this.qtyKey) || 1);
    } catch (e) {}
    this.applyQty(saved);
  }

  syncVariantId(variantId) {
    let id = variantId;
    if (!id) {
      id = new URLSearchParams(window.location.search).get('variant');
    }
    if (!id) return;

    if (this.atcId) this.atcId.value = id;
    if (this.shopId) this.shopId.value = id;
  }

  currentQty() {
    const input = this.qtyInput;
    return this.formatQty((input && input.dataset.value) || (input && input.value) || 1);
  }

  onClick(event) {
    // Labels point at main variant-picker radios (often off-screen). Without
    // preventDefault the browser focuses those inputs and scrolls the page up.
    const optionLabel = event.target.closest('.psb-new__options label[for]');
    if (optionLabel) {
      event.preventDefault();
      if (optionLabel.getAttribute('aria-disabled') === 'true') return;

      const input = document.getElementById(optionLabel.getAttribute('for'));
      if (!input || input.disabled || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (event.target.closest('[data-psb-qty-minus]')) {
      event.preventDefault();
      this.applyQty(Math.max(1, this.currentQty() - 1));
      return;
    }

    if (event.target.closest('[data-psb-qty-plus]')) {
      event.preventDefault();
      this.applyQty(this.currentQty() + 1);
      return;
    }

    if (event.target.closest('[data-psb-shop-real]')) {
      this.shopVisual?.classList.add('is-loading');
    }

    if (event.target.closest('[data-psb-atc-btn]')) {
      this.syncVariantId();
    }
  }

  onFocusIn(event) {
    if (!event.target.matches('[data-psb-qty-input]')) return;
    event.target.value = event.target.dataset.value || '1';
  }

  onFocusOut(event) {
    if (!event.target.matches('[data-psb-qty-input]')) return;
    this.applyQty(event.target.value);
  }

  onChange(event) {
    if (!event.target.matches('[data-psb-qty-input]')) return;
    this.applyQty(event.target.value);
  }

  onMouseOver(event) {
    if (event.target.closest('[data-psb-atc-btn]')) {
      this.syncVariantId();
    }
  }

  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (variant) this.syncVariantId(variant.id);
  }

  onProductRerender() {
    requestAnimationFrame(() => {
      this.restoreQty();
      this.syncVariantId();
      if (window.Shopify?.PaymentButton?.init) {
        Shopify.PaymentButton.init();
      }
    });
  }
}

if (!window.customElements.get('product-sticky-bar-new')) {
  window.customElements.define('product-sticky-bar-new', ProductStickyBarNew);
}
